import {Button, Typography} from '@sollapay/ui';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {PersonCell} from '@/components/PersonCell';
import {memberDisplayName} from '@/features/trust-accounts/members';

import {SharePowerOfAttorneyConsent} from './SharePowerOfAttorneyConsent';

import type {PowerOfAttorneyGrantor} from './SharePowerOfAttorneyConsent';
import type {TrustAccountMemberUserDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface PowerOfAttorneyConfirmProps {
  person: TrustAccountMemberUserDTO;
  grantor: PowerOfAttorneyGrantor;
  onConfirm: () => void;
  onCancel: () => void;
  isBusy?: boolean;
}

/**
 * Moving a member to operator means they act in the trustee's name, so the trustee confirms the
 * power of attorney first — checked by default, and without it the person can't become an operator.
 */
export const PowerOfAttorneyConfirm: FC<PowerOfAttorneyConfirmProps> = ({
  person,
  grantor,
  onConfirm,
  onCancel,
  isBusy = false
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const [isGranted, setIsGranted] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);
  const name = memberDisplayName(person);
  const title = t('members.powerOfAttorney.moveTitle', {name});

  useEffect(() => panelRef.current?.focus(), []);

  return (
    <div
      ref={panelRef}
      role="group"
      aria-label={title}
      tabIndex={-1}
      data-slot="power-of-attorney-confirm"
      className="flex flex-col gap-3 rounded-xl border border-brand-accent bg-background-surface p-3 outline-none"
    >
      <PersonCell
        name={title}
        initialsSource={name}
        subtitle={t(`common:roles.${person.firmRoleKey}`, {defaultValue: person.firmRoleKey})}
        avatarUrl={person.picture}
        seed={person.userId}
        emptyLabel={person.email}
        size="sm"
      />
      <SharePowerOfAttorneyConsent
        grantor={grantor}
        operatorNames={[name]}
        isGranted={isGranted}
        onGrantedChange={setIsGranted}
      />
      {!isGranted && (
        <Typography size="xs" color="tertiary" className="whitespace-normal">
          {t('members.powerOfAttorney.required', {name})}
        </Typography>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" disabled={isBusy} onClick={onCancel}>
          {t('members.powerOfAttorney.back')}
        </Button>
        <Button size="sm" disabled={!isGranted} isLoading={isBusy} onClick={onConfirm}>
          {t('members.powerOfAttorney.move')}
        </Button>
      </div>
    </div>
  );
};
